// Valeurs numériques d'un document : nombres ou expressions lues par
// l'évaluateur sûr du projet (`@jeraisonne/db/shared/expr`, sans `eval`),
// avec les paramètres comme variables. Toute valeur est BORNÉE : une
// coordonnée à ±maxCoordinate, un nombre affiché à ±maxValueMagnitude
// (« 10^308 » serait sinon une expression valide).

import { evalExpr, toEvalScope, type Scope } from '@jeraisonne/db/shared/expr';
import type { PathPart } from './issues';
import { JRAF_LIMITS as L } from './limits';
import type { JrafDocument, JrafParameter } from './schema/document';
import type { JrafObject, JrafObjectType } from './schema/objects';
import type { NumExpr } from './schema/primitives';
import type { JrafStep } from './schema/steps';
import { pathBounds } from './path-data';

export type ScopeMode = 'default' | 'min' | 'max';

/** Erreur d'évaluation : expression illisible, sans valeur finie, ou hors bornes. */
export class ExprError extends Error {
  constructor(
    message: string,
    readonly kind: 'syntax' | 'nonfinite' | 'range',
  ) {
    super(message);
    this.name = 'ExprError';
  }
}

export interface ScopeResult {
  scope: Scope;
  errors: { index: number; message: string }[];
}

/**
 * Valeurs des paramètres : défaut, ou tous au minimum, ou tous au maximum
 * (ou une valeur choisie par paramètre avec `pick`). Les constantes sont
 * calculées dans l'ordre (une constante ne voit que les paramètres écrits
 * avant elle).
 */
export function buildScope(
  parameters: readonly JrafParameter[],
  mode: ScopeMode | ((p: Extract<JrafParameter, { type: 'number' }>) => number) = 'default',
): ScopeResult {
  const scope: Scope = {};
  const errors: ScopeResult['errors'] = [];
  parameters.forEach((p, index) => {
    if (p.type === 'number') {
      scope[p.id] =
        typeof mode === 'function'
          ? mode(p)
          : mode === 'min'
            ? p.min
            : mode === 'max'
              ? p.max
              : p.default;
      return;
    }
    try {
      scope[p.id] = evalNum(p.value, scope, L.maxValueMagnitude);
    } catch (e) {
      errors.push({ index, message: (e as Error).message });
      scope[p.id] = Number.NaN;
    }
  });
  return { scope, errors };
}

/**
 * Évalue un nombre ou une expression ; lève une `ExprError` lisible si elle
 * est illisible, sans valeur finie, ou hors de [-bound ; bound].
 */
export function evalNum(value: NumExpr, scope: Scope, bound: number = L.maxCoordinate): number {
  let v: number;
  if (typeof value === 'number') v = value;
  else {
    try {
      v = evalExpr(value, toEvalScope(scope));
    } catch (e) {
      throw new ExprError(`expression « ${value} » illisible : ${(e as Error).message}`, 'syntax');
    }
  }
  if (!Number.isFinite(v))
    throw new ExprError(`expression « ${value} » sans valeur finie`, 'nonfinite');
  if (Math.abs(v) > bound)
    throw new ExprError(
      `« ${value} » vaut ${formatNumber(v)} : hors de [-${formatNumber(bound)} ; ${formatNumber(bound)}]`,
      'range',
    );
  return v;
}

function formatNumber(v: number): string {
  return Math.abs(v) >= 1e6 ? v.toExponential(2) : String(Math.round(v * 1000) / 1000);
}

export interface ParameterScenario {
  /** « paramètres par défaut », « avec k = 2 », « p au minimum, q au maximum »… */
  label: string;
  scope: Scope;
  errors: ScopeResult['errors'];
}

/**
 * Jeux de valeurs des paramètres à tester : le défaut d'abord, puis chaque
 * paramètre sur la grille de son curseur (les autres au défaut ; grille
 * échantillonnée au-delà de `maxSamplesPerParameter`), puis les coins
 * (tous les min/max croisés jusqu'à `maxParametersForCorners` paramètres,
 * sinon « tous au min » et « tous au max »).
 */
export function parameterScenarios(parameters: readonly JrafParameter[]): ParameterScenario[] {
  const nums = parameters.filter(
    (p): p is Extract<JrafParameter, { type: 'number' }> => p.type === 'number',
  );
  const out: ParameterScenario[] = [
    { label: 'paramètres par défaut', ...buildScope(parameters, 'default') },
  ];
  const fr = (v: number) => String(Math.round(v * 1e6) / 1e6).replace('.', ',');
  for (const p of nums) {
    for (const v of sliderSamples(p)) {
      if (v === p.default) continue;
      out.push({
        label: `avec ${p.id} = ${fr(v)}`,
        ...buildScope(parameters, (q) => (q.id === p.id ? v : q.default)),
      });
    }
  }
  if (nums.length >= 2) {
    const corners: Record<string, 'min' | 'max'>[] = [];
    if (nums.length <= L.maxParametersForCorners) {
      for (let mask = 0; mask < 1 << nums.length; mask++)
        corners.push(
          Object.fromEntries(nums.map((p, i) => [p.id, mask & (1 << i) ? 'max' : 'min'])),
        );
    } else {
      corners.push(Object.fromEntries(nums.map((p) => [p.id, 'min'])));
      corners.push(Object.fromEntries(nums.map((p) => [p.id, 'max'])));
    }
    for (const c of corners) {
      const sides = Object.values(c);
      const label = sides.every((s) => s === 'min')
        ? 'paramètres au minimum'
        : sides.every((s) => s === 'max')
          ? 'paramètres au maximum'
          : nums.map((p) => `${p.id} au ${c[p.id] === 'min' ? 'minimum' : 'maximum'}`).join(', ');
      out.push({ label, ...buildScope(parameters, (q) => (c[q.id] === 'max' ? q.max : q.min)) });
    }
  }
  return out;
}

/** Valeurs que peut prendre le curseur d'un paramètre (échantillonnées). */
export function sliderSamples(p: Extract<JrafParameter, { type: 'number' }>): number[] {
  if (!(p.min < p.max)) return [p.default];
  const S = L.maxSamplesPerParameter;
  const values = new Set<number>([p.min, p.max]);
  if (p.step !== undefined) {
    const n = Math.floor((p.max - p.min) / p.step + 1e-9) + 1;
    for (let i = 0; i < Math.min(n, S); i++) {
      const k = n <= S ? i : Math.round((i * (n - 1)) / (S - 1));
      values.add(Math.round((p.min + k * p.step) * 1e9) / 1e9);
    }
  } else {
    for (let i = 0; i < S; i++) values.add(p.min + ((p.max - p.min) * i) / (S - 1));
  }
  return [...values].sort((a, b) => a - b);
}

/** Champs numériques de chaque type d'objet (chemins relatifs à l'objet). */
export const OBJECT_NUMERIC_FIELDS: Record<JrafObjectType, readonly (readonly PathPart[])[]> = {
  rect: [['x'], ['y'], ['width'], ['height'], ['rx']],
  circle: [['cx'], ['cy'], ['r']],
  ellipse: [['cx'], ['cy'], ['rx'], ['ry']],
  line: [['x1'], ['y1'], ['x2'], ['y2']],
  arrow: [['x1'], ['y1'], ['x2'], ['y2']],
  polygon: [], // points : parcourus à part
  path: [],
  point: [['x'], ['y']],
  text: [
    ['at', 'x'],
    ['at', 'y'],
  ],
  formula: [
    ['at', 'x'],
    ['at', 'y'],
  ],
  value: [['at', 'x'], ['at', 'y'], ['expr']],
  image: [['x'], ['y'], ['width'], ['height']],
  group: [['x'], ['y']],
};

export interface NumField {
  path: PathPart[];
  value: NumExpr;
  /** `value` : nombre affiché (bornes larges) ; `coord` : coordonnée ou longueur */
  kind: 'coord' | 'value';
}

/** Borne d'un champ selon sa nature. */
export function fieldBound(f: Pick<NumField, 'kind'>): number {
  return f.kind === 'value' ? L.maxValueMagnitude : L.maxCoordinate;
}

function get(obj: unknown, rel: readonly PathPart[]): unknown {
  let cur = obj;
  for (const p of rel) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[p];
  }
  return cur;
}

function isNumExpr(v: unknown): v is NumExpr {
  return typeof v === 'number' || typeof v === 'string';
}

export function objectNumFields(obj: JrafObject, base: readonly PathPart[]): NumField[] {
  const out: NumField[] = [];
  for (const rel of OBJECT_NUMERIC_FIELDS[obj.type]) {
    const v = get(obj, rel);
    const kind = obj.type === 'value' && rel[0] === 'expr' ? 'value' : 'coord';
    if (isNumExpr(v)) out.push({ path: [...base, ...rel], value: v, kind });
  }
  if (obj.type === 'polygon') {
    obj.points.forEach((p, i) => {
      out.push({ path: [...base, 'points', i, 'x'], value: p.x, kind: 'coord' });
      out.push({ path: [...base, 'points', i, 'y'], value: p.y, kind: 'coord' });
    });
  }
  return out;
}

export function stepNumFields(step: JrafStep, base: readonly PathPart[]): NumField[] {
  switch (step.action) {
    case 'object.move':
      return [
        { path: [...base, 'dx'], value: step.dx, kind: 'coord' },
        { path: [...base, 'dy'], value: step.dy, kind: 'coord' },
      ];
    case 'object.scale':
    case 'object.rotate':
      return step.origin
        ? [
            { path: [...base, 'origin', 'x'], value: step.origin.x, kind: 'coord' },
            { path: [...base, 'origin', 'y'], value: step.origin.y, kind: 'coord' },
          ]
        : [];
    case 'value.set':
      return [{ path: [...base, 'expr'], value: step.expr, kind: 'value' }];
    default:
      return [];
  }
}

/** Toutes les valeurs numériques du document (objets et étapes). */
export function collectNumFields(doc: Pick<JrafDocument, 'objects' | 'scenes'>): NumField[] {
  const out: NumField[] = [];
  doc.objects.forEach((o, i) => out.push(...objectNumFields(o, ['objects', i])));
  doc.scenes.forEach((s, si) =>
    s.steps.forEach((st, ti) => out.push(...stepNumFields(st, ['scenes', si, 'steps', ti]))),
  );
  return out;
}

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Boîte englobante approchée d'un objet (sans les décalages de groupe), pour
 * signaler une figure qui sort du canevas. `null` quand on ne sait pas la
 * calculer sans rendu (chemin, texte, formule, nombre : on ne regarde que
 * leur ancre).
 */
export function objectBox(obj: JrafObject, scope: Scope): Box | null {
  const n = (v: NumExpr) => evalNum(v, scope);
  const box = (xs: number[], ys: number[]): Box => ({
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  });
  switch (obj.type) {
    case 'rect':
    case 'image': {
      const x = n(obj.x);
      const y = n(obj.y);
      return box([x, x + n(obj.width)], [y, y + n(obj.height)]);
    }
    case 'circle': {
      const r = n(obj.r);
      return box([n(obj.cx) - r, n(obj.cx) + r], [n(obj.cy) - r, n(obj.cy) + r]);
    }
    case 'ellipse': {
      const rx = n(obj.rx);
      const ry = n(obj.ry);
      return box([n(obj.cx) - rx, n(obj.cx) + rx], [n(obj.cy) - ry, n(obj.cy) + ry]);
    }
    case 'line':
    case 'arrow':
      return box([n(obj.x1), n(obj.x2)], [n(obj.y1), n(obj.y2)]);
    case 'polygon':
      return box(
        obj.points.map((p) => n(p.x)),
        obj.points.map((p) => n(p.y)),
      );
    case 'point':
      return box([n(obj.x)], [n(obj.y)]);
    case 'text':
    case 'formula':
    case 'value':
      return box([n(obj.at.x)], [n(obj.at.y)]);
    case 'path':
      return pathBounds(obj.d);
    case 'group':
      return null;
  }
}
